<template>
  <div class="min-h-screen px-6 py-8 md:px-10">
    <div class="w-full space-y-6">
      <div class="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <h1 class="mt-2 text-3xl text-gray-900">Tabulações / Etiquetas</h1>
          <p class="mt-2 max-w-2xl text-sm text-gray-500">Gerencie categorias e etiquetas utilizadas nas campanhas, chats e fluxos operacionais.</p>
        </div>

        <button
          type="button"
          class="icon-anim-trigger--back group flex h-11 items-center justify-center gap-2 rounded-xl border border-gray-200 bg-white px-5 font-medium text-gray-700 shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:border-gray-300 hover:bg-gray-50 hover:shadow-md sm:w-auto"
          @click="voltarParaConfig"
        >
          <svg class="icon-anim icon-anim--back h-5 w-5 text-gray-500 group-hover:text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15.75 19.5L8.25 12l7.5-7.5" />
          </svg>
          Voltar
        </button>
      </div>

      <div class="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
        <div class="grid grid-cols-1 gap-4 md:grid-cols-4">
          <div class="flex flex-col gap-1 md:col-span-2">
            <label class="text-sm font-medium text-gray-500">Buscar etiqueta</label>
            <input v-model="filtroBusca" type="text" placeholder="Nome da etiqueta" class="h-11 rounded-xl border border-gray-200 px-4 shadow-sm outline-none focus:ring-2 focus:ring-blue-200 focus:border-blue-300" />
          </div>

          <div v-if="temCentroCusto" class="flex flex-col gap-1">
            <label class="text-sm font-medium text-gray-500">Centro de Custo</label>
            <div class="relative group">
              <select
                id="filtro-centro"
                v-model="filtroCentro"
                class="w-full h-11 px-4 pr-10 border border-gray-200 rounded-xl bg-white bg-none text-gray-700 shadow-sm appearance-none transition-all duration-200 focus:border-blue-100 focus:ring-2 focus:ring-blue-100 cursor-pointer"
              >
                <option value="">Todos</option>
                <option v-for="centro in centroscustos" :key="centro._id" :value="centro._id">
                  {{ centro.nome }}
                </option>
              </select>

              <svg class="select-icon w-5 h-5 pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
              </svg>
            </div>
          </div>

          <div class="flex items-end gap-2 md:col-span-4 lg:col-span-1">
            <button
              @click="aplicarFiltros"
              class="h-11 w-full rounded-xl font-medium text-white px-4 transition"
              :style="{ backgroundColor: primaryColor }"
              @mouseenter="$event.target.style.backgroundColor = mixPrimaryColor('#111827', 0.2)"
              @mouseleave="$event.target.style.backgroundColor = primaryColor"
            >
              Buscar
            </button>
            <button @click="limparFiltros" class="h-11 rounded-xl border border-gray-200 bg-white px-4 font-medium text-gray-700 transition hover:bg-gray-50">Limpar</button>
          </div>
        </div>
      </div>

      <div class="mb-6 flex justify-end">
        <button @click="irParaNovaEtiqueta" class="flex items-center justify-center gap-2 h-11 rounded-xl border border-gray-200 bg-white px-5 font-medium text-gray-700 shadow-sm transition hover:shadow-md sm:w-auto">
          <svg class="h-6 w-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v6m3-3H9m12 0a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
          </svg>
          <span class="text-sm font-medium text-gray-700">Nova etiqueta</span>
        </button>
      </div>

      <div class="w-full overflow-hidden sm:rounded-lg border border-gray-200 bg-white shadow-sm">
        <div class="overflow-x-auto">
          <table class="w-full min-w-max text-sm">
            <thead class="border-b bg-gray-100 tracking-wider text-gray-600">
              <tr>
                <th class="px-6 py-4 text-left font-medium">Etiqueta</th>
                <th v-if="temCentroCusto" class="px-6 py-4 text-left font-medium">Centro de Custo</th>
                <th class="px-6 py-4 text-center font-medium">Ações</th>
              </tr>
            </thead>

            <tbody>
              <tr v-for="item in etiquetasPaginadas" :key="item._id" class="border-b border-gray-100 transition hover:bg-slate-50">
                <td class="px-6 py-5">
                  <span class="inline-flex rounded-full border px-3 py-1 text-xs font-medium" :class="item.editarCorDaEtiqueta ? '' : 'border-blue-200 bg-blue-50 text-blue-700'" :style="getTagChipStyle(item)">
                    {{ item.nome ? item.nome : "" }}
                  </span>
                </td>

                <td v-if="temCentroCusto" class="px-6 py-5 text-gray-700">
                  {{ item.centrocusto && item.centrocusto.nome ? item.centrocusto.nome : "Geral" }}
                </td>

                <td class="px-6 py-5">
                  <div class="flex flex-wrap justify-center gap-2">
                    <router-link
                      :to="`/tag/form/${item._id}`"
                      class="inline-flex h-9 items-center justify-center rounded-xl px-3 text-sm font-medium text-white transition"
                      :style="{ backgroundColor: primaryColor }"
                      @mouseenter="$event.target.style.backgroundColor = mixPrimaryColor('#111827', 0.2)"
                      @mouseleave="$event.target.style.backgroundColor = primaryColor"
                    >
                      Editar
                    </router-link>
                    <button @click="remove(item._id)" class="inline-flex h-9 items-center justify-center rounded-xl px-3 text-sm font-medium bg-red-700 text-white transition hover:bg-red-800">Remover</button>
                  </div>
                </td>
              </tr>

              <tr v-if="!etiquetasPaginadas.length">
                <td :colspan="colspanCount" class="px-6 py-10 text-center text-sm text-gray-500">Nenhuma etiqueta encontrada.</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <AppPagination
        v-if="etiquetasFiltradas.length"
        :current-page="paginaAtual"
        :total-pages="totalPaginas"
        :total-items="etiquetasFiltradas.length"
        :page-size="itensPorPagina"
        item-label="etiquetas"
        @paginate="paginaAtual = $event"
      />
    </div>
  </div>
</template>

<script>
import AppPagination from "@/components/AppPagination.vue";
import { colorMixin } from "@/utils/colorMixin";

export default {
  mixins: [colorMixin],
  components: {
    AppPagination
  },
  data() {
    return {
      filtroBusca: "",
      filtroCentro: "",
      paginaAtual: 1,
      itensPorPagina: 5,
      filtrosAplicados: {
        busca: "",
        centro: ""
      },
      list: [],
      centroscustos: []
    };
  },
  computed: {
    temCentroCusto() {
      const user = this.$store.state.user || {};
      return !!(user.lzv2 && user.lzv2.centrocusto);
    },
    colspanCount() {
      return this.temCentroCusto ? 3 : 2;
    },
    etiquetasFiltradas() {
      return this.list.filter((item) => {
        const busca = this.filtrosAplicados.busca.toLowerCase();
        const nome = (item.nome || "").toLowerCase();
        const centroNome = item.centrocusto && item.centrocusto.nome ? item.centrocusto.nome.toLowerCase() : "geral";
        const matchBusca = !busca || nome.includes(busca) || centroNome.includes(busca);
        const matchCentro = !this.filtrosAplicados.centro || (item.centrocusto && item.centrocusto._id === this.filtrosAplicados.centro);
        return matchBusca && matchCentro;
      });
    },
    totalPaginas() {
      return Math.max(1, Math.ceil(this.etiquetasFiltradas.length / this.itensPorPagina));
    },
    etiquetasPaginadas() {
      const inicio = (this.paginaAtual - 1) * this.itensPorPagina;
      return this.etiquetasFiltradas.slice(inicio, inicio + this.itensPorPagina);
    }
  },
  methods: {
    async start() {
      const etiquetasReq = await this.$http.post("/v1/tag/list", { all: true });
      this.list = etiquetasReq.data.data || [];

      if (this.temCentroCusto) {
        const centrosReq = await this.$http.post("/v1/centrocusto/list", { all: true });
        this.centroscustos = centrosReq.data.data || [];
      }
    },
    aplicarFiltros() {
      this.filtrosAplicados = {
        busca: this.filtroBusca.trim(),
        centro: this.filtroCentro
      };
      this.paginaAtual = 1;
    },
    limparFiltros() {
      this.filtroBusca = "";
      this.filtroCentro = "";
      this.filtrosAplicados = {
        busca: "",
        centro: ""
      };
      this.paginaAtual = 1;
    },
    async remove(id) {
      if (confirm("Tem certeza que deseja excluir esta etiqueta?")) {
        const resp = await this.$http.delete(`/v1/tag/${id}`);
        if (!resp.data.success) {
          this.$vToastify.error(resp.data.err);
          return;
        }
        this.$vToastify.success("Etiqueta removida");
        this.start();
      }
    },
    getTagChipStyle(tag) {
      if (!tag || !tag.editarCorDaEtiqueta || !tag.corDaEtiqueta) {
        return {};
      }

      const color = this.normalizeHexColor(tag.corDaEtiqueta);
      if (!color) {
        return {};
      }

      return {
        backgroundColor: color,
        borderColor: color,
        color: this.isLightColor(color) ? "#1f2937" : "#ffffff"
      };
    },
    normalizeHexColor(color) {
      const value = String(color || "").trim();
      const shortHex = /^#([0-9a-fA-F]{3})$/;
      const longHex = /^#([0-9a-fA-F]{6})$/;

      if (shortHex.test(value)) {
        const hex = value.slice(1);
        return `#${hex[0]}${hex[0]}${hex[1]}${hex[1]}${hex[2]}${hex[2]}`;
      }

      if (longHex.test(value)) {
        return value;
      }

      return null;
    },
    isLightColor(hexColor) {
      const hex = hexColor.slice(1);
      const r = parseInt(hex.substring(0, 2), 16);
      const g = parseInt(hex.substring(2, 4), 16);
      const b = parseInt(hex.substring(4, 6), 16);
      const luminance = 0.299 * r + 0.587 * g + 0.114 * b;

      return luminance > 186;
    },
    voltarParaConfig() {
      this.$router.push({ name: "Config" });
    },
    irParaNovaEtiqueta() {
      this.$router.push({ name: "TagForm" });
    }
  },
  beforeMount() {
    this.start();
  }
};
</script>

<style scoped>
:deep(.heroicon-ui) {
  fill: currentColor !important;
}

.icon-anim {
  transition: transform 0.2s ease;
}

.icon-anim-trigger--back:hover .icon-anim--back,
.icon-anim-trigger--back:focus-visible .icon-anim--back {
  transform: translateX(-4px);
}

.select-icon {
  position: absolute;
  right: 0.75rem;
  top: 50%;
  pointer-events: none;
  color: #9ca3af;
  transform: translateY(-50%);
  transition: transform 0.2s ease, color 0.2s ease;
}

.group:focus-within .select-icon {
  color: #6366f1;
  transform: translateY(-50%) rotate(180deg);
}
</style>
